import React, { useMemo, useState } from 'react';
import { Button, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { connect } from 'react-redux';
import useAxios from 'axios-hooks';
import { IWorkstation } from '@relab/alstom-ptw-shared';

import { workstationUpsertAction } from '../../redux/actions';
import { TranslationComponent } from '../TranslationComponent';
import { useTranslation } from 'react-i18next';
import { CreateWorkstationSchema } from './WorkstationSchema';

interface WorkstationEditModalProps {
  workstation: IWorkstation;
  workstationUpsertAction: typeof workstationUpsertAction;
}

const PWorkstationEditModal: React.FC<WorkstationEditModalProps> = ({ workstation, workstationUpsertAction }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { t } = useTranslation();

  const validationSchema = useMemo(() => CreateWorkstationSchema(t), [t]);

  const [{ loading, error }, executeEdit] = useAxios(
    {
      url: `/workstation`,
      method: 'POST',
    },
    { manual: true }
  );

  const handleModalOpen = () => setModalOpen(true);

  const handleModalClose = () => setModalOpen(false);

  const handleSubmit = (values: Partial<IWorkstation>) => {
    const { name } = values;

    executeEdit({
      data: {
        ...workstation,
        name,
      },
    }).then(({ data }) => {
      workstationUpsertAction(data);
      handleModalClose();
    });
  };

  return (
    <Formik
      initialValues={{
        name: workstation.name,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Modal
          trigger={
            <Button icon onClick={handleModalOpen}>
              <Icon name="pencil alternate" />
            </Button>
          }
          open={modalOpen}
          onClose={handleModalClose}
          closeOnDimmerClick={false}
        >
          <Modal.Header>
            <TranslationComponent stringKey={'Edit Workstation'} />
          </Modal.Header>
          <Modal.Content>
            <Form id="editWorkstationForm" size="large" error loading={isSubmitting && loading} onSubmit={handleSubmit}>
              <Modal.Description>
                <Field name="name">
                  {({ field }: FieldProps) => <Form.Input placeholder={t('Name')} fluid {...field} />}
                </Field>
                <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                {error && <Message error content={error.response.data} />}
              </Modal.Description>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={handleModalClose}>
              <TranslationComponent stringKey={'Cancel'} />
            </Button>
            <Button primary form="editWorkstationForm" type="submit">
              <TranslationComponent stringKey={'Confirm'} />
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </Formik>
  );
};

export const WorkstationEditModal = connect(null, { workstationUpsertAction })(PWorkstationEditModal);
